import React from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import '../../../styles/syntax-highlight.css'

type MarkdownProps<T extends keyof React.JSX.IntrinsicElements> =
  ComponentPropsWithoutRef<T> & { node?: unknown }

/**
 * Markdown components configuration
 *
 * Extracted as const to avoid recreating on every render.
 */
const MARKDOWN_COMPONENTS = {
  h1: ({ children, ...props }: MarkdownProps<'h1'>) => (
    <h1 className="text-base font-bold mt-3 mb-1.5" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }: MarkdownProps<'h2'>) => (
    <h2 className="text-sm font-bold mt-2.5 mb-1.5" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }: MarkdownProps<'h3'>) => (
    <h3 className="text-sm font-semibold mt-2 mb-1" {...props}>
      {children}
    </h3>
  ),
  p: ({ children, ...props }: MarkdownProps<'p'>) => (
    <p className="mb-2 leading-relaxed font-normal" {...props}>
      {children}
    </p>
  ),
  ul: ({ children, ...props }: MarkdownProps<'ul'>) => (
    <ul className="list-disc ml-4 mb-2 space-y-0.5" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: MarkdownProps<'ol'>) => (
    <ol className="list-decimal ml-4 mb-2 space-y-0.5" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }: MarkdownProps<'li'>) => (
    <li className="text-sm font-normal" {...props}>
      {children}
    </li>
  ),
  code: ({ className, children, ...props }: MarkdownProps<'code'>) => {
    const isInline = !className
    return isInline ? (
      <code
        className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-xs font-mono"
        {...props}
      >
        {children}
      </code>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    )
  },
  pre: ({ children, ...props }: MarkdownProps<'pre'>) => (
    <pre
      className="bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2.5 rounded-lg overflow-x-auto my-2 text-xs border border-gray-200 dark:border-gray-700"
      {...props}
    >
      {children}
    </pre>
  ),
  a: ({ children, ...props }: MarkdownProps<'a'>) => (
    <a
      className="text-brand-500 hover:text-brand-600 underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
  table: ({ children, ...props }: MarkdownProps<'table'>) => (
    <div className="overflow-x-auto my-2">
      <table
        className="min-w-full border border-gray-300 dark:border-gray-700 text-xs"
        {...props}
      >
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }: MarkdownProps<'th'>) => (
    <th
      className="border border-gray-300 dark:border-gray-700 px-2 py-1.5 bg-gray-100 dark:bg-gray-800 font-semibold text-left text-xs"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }: MarkdownProps<'td'>) => (
    <td
      className="border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-xs"
      {...props}
    >
      {children}
    </td>
  ),
  blockquote: ({ children, ...props }: MarkdownProps<'blockquote'>) => (
    <blockquote
      className="border-l-4 border-gray-400 dark:border-gray-600 pl-3 my-2 italic text-gray-700 dark:text-gray-300 text-sm"
      {...props}
    >
      {children}
    </blockquote>
  ),
  hr: (props: MarkdownProps<'hr'>) => (
    <hr className="my-2 border-gray-300 dark:border-gray-700" {...props} />
  ),
} as const

export interface MarkdownRendererProps {
  content: string
  className?: string
}

/**
 * Markdown renderer with syntax highlighting and custom styling
 *
 * Features:
 * - GFM (GitHub Flavored Markdown) support
 * - Syntax highlighting for code blocks
 * - Custom styled components for all markdown elements
 * - Dark mode support
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = '',
}) => {
  return (
    <div className={`markdown-content font-normal ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={MARKDOWN_COMPONENTS}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
